import { useEffect, useRef, useState } from 'react';
import type {
  ChatPayload,
  IntakeResponse,
  Question,
} from '../api/types';
import { usePostIntake } from '../hooks/usePostIntake';
import { useTranslation } from '../i18n';
import { useIntake } from '../state/IntakeContext';
import { ChatBubble } from './ChatBubble';
import { ChatInput, type ChatInputSubmit } from './ChatInput';
import { ProgressBar } from './ProgressBar';
import { ThemeToggle } from './ThemeToggle';
import { TypingIndicator } from './TypingIndicator';
import './ChatScreen.css';

/** Step 8 of the flow. Renders the chat transcript, a progress bar, and the
 *  input area. When Gemma emits a constrained question, the input swaps
 *  controls automatically. When `complete: true`, advances to review phase. */
export function ChatScreen() {
  const { t } = useTranslation();
  const { state, setPhase } = useIntake();
  const { send, loading, error, lastResponse, clearError } = usePostIntake();

  // Current question + progress come from the most recent response. If
  // there hasn't been one yet (refresh after upload/confirm), we fall back
  // to derived progress from state and no question.
  const [question, setQuestion] = useState<Question | null>(
    lastResponse?.question ?? null,
  );
  const [progress, setProgress] = useState(() => ({
    confirmed: Object.keys(state.confirmed).length,
    required_total:
      state.required_pending.length + Object.keys(state.confirmed).length,
  }));
  // Glossary keys flagged on the most recent assistant reply. Only the
  // latest assistant bubble gets decorated; older messages render plain
  // (they were decorated when they were the latest).
  const [latestTermsUsed, setLatestTermsUsed] = useState<readonly string[]>(
    lastResponse?.extras?.terms_used ?? [],
  );

  // Kick off the chat if we haven't yet. Happens on the "skip upload" path
  // where the chat phase lands without any prior Worker reply, and also on
  // a mid-session refresh with no cached lastResponse.
  const hasKickedOff = useRef(false);
  useEffect(() => {
    if (hasKickedOff.current) return;
    // If history already has an assistant turn (e.g. from the confirm
    // response), we don't need to kick off — just render.
    const hasAssistantTurn = state.history.some((t) => t.role === 'assistant');
    if (hasAssistantTurn) {
      hasKickedOff.current = true;
      return;
    }
    hasKickedOff.current = true;
    void kickoff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function kickoff() {
    try {
      const res = await send<ChatPayload>({
        action: 'chat',
        payload: { message: '' },
      });
      applyResponse(res);
    } catch {
      /* surfaced via `error` */
    }
  }

  async function handleSubmit(r: ChatInputSubmit) {
    let payload: ChatPayload;
    if (r.message !== undefined) {
      payload = { message: r.message };
    } else if (r.answer !== undefined) {
      payload = { answer: r.answer };
    } else {
      return;
    }

    try {
      const res = await send<ChatPayload>({ action: 'chat', payload });
      applyResponse(res);
    } catch {
      /* surfaced via `error` */
    }
  }

  function applyResponse(res: IntakeResponse) {
    setQuestion(res.question);
    setProgress(res.progress);
    setLatestTermsUsed(res.extras?.terms_used ?? []);
    if (res.complete) {
      setPhase('review');
    }
  }

  // Auto-scroll chat list to bottom on new messages.
  const listRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [state.history, loading]);

  return (
    <main id="main" className="cf-chat-screen">
      <header className="cf-chat-header">
        <ProgressBar
          confirmed={progress.confirmed}
          requiredTotal={progress.required_total}
        />
        <ThemeToggle />
      </header>

      <ol
        className="cf-chat-list"
        ref={listRef}
        aria-label={t('a11y.chat_messages_region')}
        aria-live="polite"
        aria-relevant="additions text"
      >
        {state.history.map((turn, i) => {
          const isLatestAssistant =
            turn.role === 'assistant' &&
            i === lastAssistantIndex(state.history);
          return (
            <li
              key={`${i}:${turn.role}:${turn.content.length}`}
              className="cf-chat-item"
            >
              <ChatBubble
                turn={turn}
                termsUsed={isLatestAssistant ? latestTermsUsed : undefined}
              />
            </li>
          );
        })}
        {loading && (
          <li className="cf-chat-item">
            <TypingIndicator />
          </li>
        )}
      </ol>

      {error && (
        <div className="cf-chat-error" role="alert">
          <span aria-hidden="true">⚠</span>
          <span>{t('chat.error_network')}</span>
          <button
            type="button"
            className="cf-btn cf-btn--ghost"
            onClick={clearError}
          >
            {t('chat.error_retry')}
          </button>
        </div>
      )}

      <ChatInput
        question={question}
        disabled={loading}
        onSubmit={handleSubmit}
      />
    </main>
  );
}

function lastAssistantIndex(history: readonly { role: string }[]): number {
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i]?.role === 'assistant') return i;
  }
  return -1;
}
